import React from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Armchair, Check, Ban } from "lucide-react";

interface VenueSection {
  id: string;
  name: string;
  color: string;
  price: number;
  rows: {
    id: string;
    rowNumber: string;
    seatCount: number;
  }[];
}

interface Stage {
  position: 'top' | 'bottom' | 'left' | 'right';
  name: string;
}

interface VenueLayout {
  sections: VenueSection[];
  stage?: Stage;
}

interface InteractiveVenueMapProps {
  venueLayout: VenueLayout;
  takenSeats: {
    section: string;
    row_number: string;
    seat_number: string;
    phone?: string;
  }[];
  selectedSeat?: {
    section: string;
    rowNumber: string;
    seatNumber: string;
  } | {
    section: string;
    rowNumber: string;
    seatNumber: string;
  }[] | null;
  onSeatSelect: (section: string, rowNumber: string, seatNumber: string) => void;
}

export const InteractiveVenueMap = ({ 
  venueLayout, 
  takenSeats, 
  selectedSeat,
  onSeatSelect 
}: InteractiveVenueMapProps) => {
  const isSeatTaken = (sectionId: string, rowNumber: string, seatNumber: string) => {
    const seat = takenSeats.find(
      s => s.section === sectionId && 
           s.row_number === rowNumber && 
           s.seat_number === seatNumber
    );
    return seat !== undefined;
  };

  const isSeatAdminBlocked = (sectionId: string, rowNumber: string, seatNumber: string) => {
    const seat = takenSeats.find(
      s => s.section === sectionId && 
           s.row_number === rowNumber && 
           s.seat_number === seatNumber
    );
    // Admin blocked seats have phone format: +374-{section}-{row}-{seat}
    return seat && seat.phone?.startsWith(`+374-${sectionId}-${rowNumber}-${seatNumber}`);
  };

  const isSeatSelected = (sectionId: string, rowNumber: string, seatNumber: string) => {
    if (!selectedSeat) return false;
    
    // Support both single seat and array of seats
    if (Array.isArray(selectedSeat)) {
      return selectedSeat.some(
        s => s.section === sectionId && 
             s.rowNumber === rowNumber && 
             s.seatNumber === seatNumber
      );
    }
    
    return selectedSeat.section === sectionId && 
           selectedSeat.rowNumber === rowNumber && 
           selectedSeat.seatNumber === seatNumber;
  };

  return (
    <div className="space-y-6">
      {/* Stage Display */}
      {venueLayout.stage && (
        <div className="flex justify-center">
          <div className="bg-amber-500/20 border-2 border-amber-500 rounded-lg px-12 py-4 text-center">
            <div className="text-2xl mb-1">🎭 ԲԵՄ</div>
            <div className="font-semibold text-sm">{venueLayout.stage.name}</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-primary/20 border-2 border-primary flex items-center justify-center">
            <Check className="w-4 h-4 text-primary" />
          </div>
          <span>Ընտրված</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-muted" />
          <span>Ազատ</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-destructive/20 opacity-50" />
          <span>Զբաղված</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-red-500/30 border-2 border-red-500 flex items-center justify-center">
            <Ban className="w-4 h-4 text-red-500" />
          </div>
          <span>Ադմինի կողմից բլոկավորված</span>
        </div>
      </div>

      <div className="space-y-8">
        {venueLayout.sections.map((section) => (
          <Card key={section.id} className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-6 h-6 rounded" 
                  style={{ backgroundColor: section.color }}
                />
                <h3 className="font-semibold text-lg">{section.name}</h3>
              </div>
              <Badge variant="secondary">
                {section.price.toLocaleString()} դրամ
              </Badge>
            </div>

            <div className="space-y-3">
              {section.rows.map((row) => (
                <div key={row.id} className="flex items-center gap-3">
                  <div className="w-12 text-center font-medium text-sm">
                    {row.rowNumber}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: row.seatCount }, (_, i) => {
                      const seatNumber = (i + 1).toString();
                      const taken = isSeatTaken(section.id, row.rowNumber, seatNumber);
                      const selected = isSeatSelected(section.id, row.rowNumber, seatNumber);
                      const adminBlocked = isSeatAdminBlocked(section.id, row.rowNumber, seatNumber);

                      return (
                        <button
                          key={i}
                          onClick={() => onSeatSelect(section.id, row.rowNumber, seatNumber)}
                          disabled={taken}
                          className={`
                            w-8 h-8 rounded flex items-center justify-center text-xs font-medium
                            transition-all duration-200 relative
                            ${adminBlocked
                              ? 'bg-red-500/30 border-2 border-red-500 cursor-not-allowed'
                              : taken 
                                ? 'bg-destructive/20 opacity-50 cursor-not-allowed' 
                                : selected
                                  ? 'bg-primary/20 border-2 border-primary text-primary scale-110'
                                  : 'bg-muted hover:bg-muted/80 hover:scale-105 cursor-pointer'
                            }
                          `}
                          title={
                            adminBlocked 
                              ? `Ադմինի կողմից բլոկավորված - Շարք ${row.rowNumber}, Նստատեղ ${seatNumber}`
                              : `Շարք ${row.rowNumber}, Նստատեղ ${seatNumber}`
                          }
                        >
                          {adminBlocked ? (
                            <Ban className="w-4 h-4 text-red-500" />
                          ) : selected ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Armchair className="w-4 h-4" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
